import { fetchJson } from '~/lib/fetch';
import type { DiscussionSort, DiscussionFilter } from './types';

interface SearchRequestParams {
  query: string;
  deal_id: number;
  comment_type: 'dealreview' | 'dealcomment';
}

interface ListRequestParams {
  sort: 'date' | 'helpful' | 'rating';
  order: 'asc' | 'desc';
  group: 'dealreview' | 'dealcomment';
  moderator_filter?: 'unapproved' | 'unresolved';
  page: number;
  for_nuxt_pdp: 'True';
}

/**
 * Construct the URL for fetching comments from the discussions API.
 */
export function getDiscussionsUrl({
  dealId,
  type,
  page = 1,
  sort = 'latest',
  filter = 'all',
  query,
}: {
  dealId: number;
  type: 'dealreview' | 'dealcomment';
  page?: number;
  sort?: DiscussionSort;
  filter?: DiscussionFilter;
  query?: string;
}) {
  if (query) {
    const requestParams: SearchRequestParams = {
      query,
      comment_type: type,
      deal_id: dealId,
    };

    const requestString = Object.keys(requestParams)
      .map(
        (key: string) =>
          `${key}=${encodeURIComponent(
            requestParams[key as keyof SearchRequestParams] as string,
          )}`,
      )
      .join('&');

    return `/api/v2/deals/search_comments/?${requestString}`;
  } else {
    const requestParams: ListRequestParams = {
      group: type,
      page: page,
      for_nuxt_pdp: 'True',
      sort: 'date',
      order: 'desc',
    };

    switch (sort) {
      case 'latest':
      case 'newest':
        (requestParams['sort'] = 'date'), (requestParams['order'] = 'desc');
        break;

      case 'oldest':
        (requestParams['sort'] = 'date'), (requestParams['order'] = 'asc');
        break;

      case 'helpful':
        requestParams['sort'] = 'helpful';
        break;

      case 'rating:asc':
        (requestParams['sort'] = 'rating'), (requestParams['order'] = 'asc');
        break;

      case 'rating:desc':
        (requestParams['sort'] = 'rating'), (requestParams['order'] = 'desc');
        break;
    }

    switch (filter) {
      case 'unapproved':
        requestParams['moderator_filter'] = 'unapproved';
        break;

      case 'unresolved':
        requestParams['moderator_filter'] = 'unresolved';
        break;
    }

    const requestString = Object.keys(requestParams)
      .map(
        (key) =>
          `${key}=${encodeURIComponent(
            requestParams[key as keyof ListRequestParams] as string,
          )}`,
      )
      .join('&');

    return `/discussions/api/listcomments/${dealId}/?${requestString}`;
  }
}

/**
 * Fetch comments from the discussions API.
 */
export async function getComments(url: string) {
  return fetchJson(url).then((data) => {
    return data?.comments || [];
  });
}
